// @ts-nocheck

// App
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import PriceInput from 'app/shared/modules/MobileSRP/Header/FilterModules/PriceFilter/PriceInput';
import SliderThumb from 'app/shared/modules/CollectionResultPage/PriceFilter/SliderThumb';
import SliderTrack from 'app/shared/modules/CollectionResultPage/PriceFilter/SliderTrack';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import { PRICE_SLIDER_MAX } from 'app/shared/constants/FilterConstants';
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';
import {
  emitTrackHighPriceFilterEvent,
  emitTrackLowPriceFilterEvent,
} from 'app/shared/models/Clickstream/FilterClickstreamEvents';
import { HOMEPAGE_FILTER_AUTOMATION_IDS } from 'app/shared/modules/location/AutocompleteSearchInput/automation';

const HUNDRED_TO_ONE_SCALE = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;

export const blurFocusedElementWithin = (container) => {
  const activeElement = document.activeElement;
  if (container && activeElement instanceof HTMLElement && container.contains(activeElement)) {
    activeElement.blur();
  }
};

export const getPendingPriceStateFromContainer = ({ container, sliderValue }) => {
  const readInputState = (selector) => {
    const input = container?.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) {
      return null;
    }

    if (input.value === '') {
      return { isEmpty: true };
    }

    const parsedValue = Number(input.value);
    if (Number.isNaN(parsedValue)) {
      return null;
    }

    // Keep the exact typed dollars so values like $1,750 aren't floored to the slider grid
    return { dollars: parsedValue };
  };

  const minState = readInputState('#min-price-input');
  const maxState = readInputState('#max-price-input');

  const nextSliderValue = [
    minState === null ? sliderValue[0] : minState.isEmpty ? 0 : minState.dollars / HUNDRED_TO_ONE_SCALE,
    maxState === null ? sliderValue[1] : maxState.isEmpty ? PRICE_SLIDER_MAX : maxState.dollars / HUNDRED_TO_ONE_SCALE,
  ];

  const getMin = () => {
    if (minState?.dollars != null) {
      return minState.dollars === 0 ? null : minState.dollars;
    }
    return nextSliderValue[0] === 0 ? null : Math.round(nextSliderValue[0] * HUNDRED_TO_ONE_SCALE);
  };

  const getMax = () => {
    // An explicitly typed max is kept even at the slider-top price ($10,000); only the
    // untouched/emptied top position means "no max"
    if (maxState?.dollars != null) {
      return maxState.dollars;
    }
    return nextSliderValue[1] === PRICE_SLIDER_MAX ? null : Math.round(nextSliderValue[1] * HUNDRED_TO_ONE_SCALE);
  };

  return {
    sliderValue: nextSliderValue,
    priceState: {
      min: getMin(),
      max: getMax(),
    },
  };
};

/**
 * Price slider - A component that contains display and business logic for the price filter
 * @param {Object} props - props object
 * @param {boolean} props.showResetButton [showResetButton=false] - Show or hide reset button
 * @param {boolean} props.preventDefault [preventDefault=false] - if true, only carry out Redux filter change (do not fecth listings)
 * @param {string} props.customTrackingEvent [customTrackingEvent=null] - if present, use event instead of default filter event
 * @returns component
 */
const PriceSlider = forwardRef(
  (
    {
      showResetButton = false,
      preventDefault = false,
      customTrackingEvent = null,
      triggerLocation = null,
      triggerObject = null,
    },
    ref,
  ) => {
    // Redux
    const price = useSelector((state) => state.filter.price);
    const containerRef = useRef(null);

    // React state
    const [priceState, setPriceState] = useState(price);
    const [sliderValue, setSliderValue] = useState([
      priceState.min ? priceState.min / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : 0,
      priceState.max ? priceState.max / VALUE_CONSTANTS.PRICE_FILTER_INCREMENT : PRICE_SLIDER_MAX,
    ]);

    // Hooks
    const { filterChange, reduxOnlyChange, resetFilter, isFilterResetting } = useFilterChange();

    const handlePriceChange = useCallback(
      (rawVals, options = {}) => {
        const opts = typeof options === 'boolean' ? { isPriceSetViaInput: options } : options;
        const { isMaxChangeOnly, isMaxValueUnset, isMinChangeOnly, isMinValueUnset, isPriceSetViaInput } = opts;
        const [min, max] = rawVals;
        const MULTIPLIER = VALUE_CONSTANTS.PRICE_FILTER_INCREMENT;
        // Exact typed dollars bypass the slider-position round-trip so values like $1,750 aren't floored to the grid
        const getFormattedMin = () =>
          min === 0 || isMinValueUnset ? null : (opts.minExactDollars ?? Math.round(min * MULTIPLIER));
        const getFormattedMax = () => {
          // The max thumb sitting at the slider top means "no max" unless the max input explicitly set it
          if (max === PRICE_SLIDER_MAX && (!isPriceSetViaInput || isMaxValueUnset)) {
            return null;
          }

          return opts.maxExactDollars ?? Math.round(max * MULTIPLIER);
        };
        // A change to one side must leave the other side's committed price untouched
        const formattedVals = {
          min: isMaxChangeOnly ? priceState.min : getFormattedMin(),
          max: isMinChangeOnly ? priceState.max : getFormattedMax(),
        };
        setPriceState({ min: formattedVals.min, max: formattedVals.max });

        if (formattedVals.min !== priceState.min) {
          emitTrackLowPriceFilterEvent({ triggerLocation, triggerObject });
        } else if (formattedVals.max !== priceState.max) {
          emitTrackHighPriceFilterEvent({ triggerLocation, triggerObject });
        }
      },
      [priceState, setPriceState],
    );

    const handleSliderOnChange = useCallback((valArr) => {
      setSliderValue(valArr);
    }, []);

    const handleSliderOnAfterChange = useCallback(
      (valArr, thumbIndex) => {
        // Only the dragged thumb's value should change; the other side keeps its committed price
        handlePriceChange(valArr, thumbIndex === 0 ? { isMinChangeOnly: true } : { isMaxChangeOnly: true });
      },
      [handlePriceChange],
    );

    const handleSliderMinValueChange = useCallback(
      (val, { isValueUnset, exactDollars } = {}) => {
        const units = exactDollars != null ? exactDollars / HUNDRED_TO_ONE_SCALE : val;
        const valArr = [units, sliderValue[1]];
        setSliderValue(valArr);
        handlePriceChange(valArr, {
          isMinChangeOnly: true,
          isMinValueUnset: isValueUnset,
          isPriceSetViaInput: true,
          minExactDollars: exactDollars,
        });
      },
      [sliderValue, handlePriceChange],
    );
    const handleSliderMaxValueChange = useCallback(
      (val, { isValueUnset, exactDollars } = {}) => {
        const units = exactDollars != null ? exactDollars / HUNDRED_TO_ONE_SCALE : val;
        const valArr = [sliderValue[0], units];
        setSliderValue(valArr);
        handlePriceChange(valArr, {
          isMaxChangeOnly: true,
          isMaxValueUnset: isValueUnset,
          isPriceSetViaInput: true,
          maxExactDollars: exactDollars,
        });
      },
      [sliderValue, handlePriceChange],
    );

    const ariaValueText = useCallback((state) => {
      return `Current ${state.index === 0 ? 'min' : 'max'} value: ${
        state.valueNow === 0 ? 'No minimum' : `${state.valueNow * VALUE_CONSTANTS.PRICE_FILTER_INCREMENT} dollars`
      }`;
    }, []);

    const handleResetClick = useCallback(() => {
      resetFilter({ price: DEFAULT.price, preventDefault });
      setPriceState(DEFAULT.price);
      setSliderValue([0, PRICE_SLIDER_MAX]);

      // resetFilter is not memoized in useFilterChange but only depends on dispatch (stable)
    }, []);

    const syncPendingInput = useCallback(() => {
      const pendingPrice = getPendingPriceStateFromContainer({
        container: containerRef.current,
        sliderValue,
      });
      // An explicitly typed slider-top price ($10,000) changes priceState without moving a thumb,
      // so compare both
      const didPendingPriceChange =
        pendingPrice.sliderValue[0] !== sliderValue[0] ||
        pendingPrice.sliderValue[1] !== sliderValue[1] ||
        pendingPrice.priceState.min !== priceState.min ||
        pendingPrice.priceState.max !== priceState.max;

      if (didPendingPriceChange) {
        setSliderValue(pendingPrice.sliderValue);
        setPriceState(pendingPrice.priceState);

        if (preventDefault) {
          reduxOnlyChange({ price: pendingPrice.priceState });
        } else {
          filterChange({ price: pendingPrice.priceState });
        }
      }

      blurFocusedElementWithin(containerRef.current);
      return pendingPrice.priceState;

      // reduxOnlyChange/filterChange are stable dispatch wrappers from useFilterChange.
    }, [preventDefault, sliderValue, priceState]);

    useImperativeHandle(
      ref,
      () => ({
        handleInputBlur() {
          syncPendingInput();
        },
        syncPendingInput,
      }),
      [syncPendingInput],
    );

    // Listens for filter changes => updates Redux reducer
    useEffect(() => {
      if (!isFilterResetting) {
        if (preventDefault) {
          reduxOnlyChange({ price: priceState });
        } else {
          filterChange({ price: priceState });
        }
      }

      // reduxOnlyChange/filterChange not memoized in useFilterChange but only depend on dispatch (stable)
      // Including them causes infinite re-render loop. See HPWEB-6645
    }, [priceState, isFilterResetting, preventDefault]);

    return (
      <div ref={containerRef}>
        <GeneralFilterSection>
          <GeneralFilterHeader>
            <GeneralFilterLabel id="price-label">Price range</GeneralFilterLabel>
            {showResetButton && (
              <GeneralFilterResetButton onClick={handleResetClick} aria-label={'Reset all active price filters'}>
                Reset
              </GeneralFilterResetButton>
            )}
          </GeneralFilterHeader>

          <S.InputContainer>
            <PriceInput
              ariaLabel={'Set minimum price'}
              commitOnChange={preventDefault}
              data-name={preventDefault ? 'HomepagePriceMinInput' : undefined}
              data-testid={preventDefault ? HOMEPAGE_FILTER_AUTOMATION_IDS.priceMinInput : undefined}
              handleSliderValueChange={handleSliderMinValueChange}
              id={'min-price-input'}
              isMinInput
              isValueUnset={sliderValue[0] === 0 && priceState.min === null}
              name={'min-price-input'}
              placeholder={'No min'}
              value={Math.round(sliderValue[0] * HUNDRED_TO_ONE_SCALE)}
            />
            <S.RangeText aria-hidden="true">to</S.RangeText>
            <PriceInput
              ariaLabel={'Set maximum price'}
              commitOnChange={preventDefault}
              data-name={preventDefault ? 'HomepagePriceMaxInput' : undefined}
              data-testid={preventDefault ? HOMEPAGE_FILTER_AUTOMATION_IDS.priceMaxInput : undefined}
              handleSliderValueChange={handleSliderMaxValueChange}
              id={'max-price-input'}
              isMaxInput
              isValueUnset={sliderValue[1] === PRICE_SLIDER_MAX && priceState.max === null}
              name={'max-price-input'}
              placeholder={'No max'}
              value={Math.round(sliderValue[1] * HUNDRED_TO_ONE_SCALE)}
            />
          </S.InputContainer>
          <S.PriceSlider
            aria-labelledby="price-info-card-label"
            ariaLabel={['Minimum price thumb', 'Maximum price thumb']}
            ariaValuetext={ariaValueText}
            min={0} // 1:100 scale
            max={PRICE_SLIDER_MAX} // 1:100 scale
            value={sliderValue} // 1:100 scale
            onChange={handleSliderOnChange}
            onAfterChange={handleSliderOnAfterChange}
            renderTrack={SliderTrack}
            renderThumb={SliderThumb}
          />
        </GeneralFilterSection>
      </div>
    );
  },
);

PriceSlider.displayName = 'PriceSlider';

export default PriceSlider;
